import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import SendbirdChat, { SessionHandler } from '@sendbird/chat'
import { GroupChannelModule } from '@sendbird/chat/groupChannel'

import { TELUS_ASYNC_CHAT_API_ID } from '~/constants'

type SendbirdInstance = SendbirdChat & {
  groupChannel: {
    getTotalUnreadMessageCount: () => Promise<number>
  }
}

type RetrieveTokenFn = () => Promise<string | null>

// Singleton Sendbird SDK instance, separate from the UIKit provider in
// SendBirdConnectionAuth. This allows us to make REST-style SDK calls
// (e.g. unread counts) without requiring the UIKit context.
let instance: SendbirdInstance | null = null
let currentUserId: string | null = null
// Stored by reference so the session handler always calls the latest
// version, even if the React component re-renders with a new callback.
let latestRetrieveToken: RetrieveTokenFn

const getInstance = (retrieveToken: RetrieveTokenFn): SendbirdInstance => {
  latestRetrieveToken = retrieveToken

  if (!instance) {
    instance = SendbirdChat.init({
      appId: TELUS_ASYNC_CHAT_API_ID,
      modules: [new GroupChannelModule()],
    }) as SendbirdInstance

    const sessionHandler = new SessionHandler()

    // Called by the SDK when the current session token is about to expire.
    // We fetch a fresh token via our GraphQL mutation and hand it back.
    sessionHandler.onSessionTokenRequired = (resolve, reject) => {
      latestRetrieveToken()
        .then((newToken) => {
          if (newToken) {
            resolve(newToken)
          } else {
            const err = new Error('Failed to retrieve Sendbird session token')
            datadogRum.addError(err, {
              source: 'sendbird-rest-client',
              action: 'session_token_refresh',
            })
            reject(err)
          }
        })
        .catch((err) => {
          datadogRum.addError(err, {
            source: 'sendbird-rest-client',
            action: 'session_token_refresh',
          })
          reject(err)
        })
    }

    // Session was revoked server-side; clear state so the next
    // call re-authenticates from scratch.
    sessionHandler.onSessionClosed = () => {
      datadogLogs.logger.warn(
        '[SendbirdRestClient] Session closed by server — will re-authenticate on next request'
      )
      currentUserId = null
    }

    instance.setSessionHandler(sessionHandler)
  }
  return instance
}

/**
 * Disconnects the singleton Sendbird REST client and clears all module-level
 * state. Should be called on user logout so that a subsequent login always
 * starts with a clean, unauthenticated instance rather than inheriting the
 * previous user's session.
 */
export const disconnectSendbirdRestClient = async (): Promise<void> => {
  const instanceToDisconnect = instance

  // Clear module state synchronously so that any concurrent in-flight call
  // that hasn't awaited this function yet sees a clean slate immediately,
  // regardless of how long the network disconnect takes.
  instance = null
  currentUserId = null

  if (instanceToDisconnect?.currentUser) {
    try {
      await instanceToDisconnect.disconnect()
    } catch (err) {
      // Best-effort disconnect — don't block the logout flow if this fails.
      datadogLogs.logger.warn(
        '[SendbirdRestClient] disconnect failed during logout',
        { err }
      )
    }
  }
}

/**
 * Fetches the total unread message count for a user. Handles authentication
 * lazily — only connects when the user changes or no session exists, to
 * avoid "User auth must be expiring session" errors from re-authenticating
 * an already-active session.
 */
export const fetchTotalUnreadMessageCount = async (
  userId: string,
  token: string,
  retrieveToken: RetrieveTokenFn
): Promise<number> => {
  const sb = getInstance(retrieveToken)

  // Only authenticate when the user has changed or there is no
  // active session.  Re-authenticating an already-active session
  // causes a "User auth must be expiring session" error.
  if (!sb.currentUser || currentUserId !== userId) {
    if (sb.currentUser) {
      await sb.disconnect()
    }
    try {
      await sb.authenticate(userId, token)
    } catch (err) {
      datadogRum.addError(err as Error, {
        source: 'sendbird-rest-client',
        action: 'authenticate',
      })
      throw err
    }
    currentUserId = userId
  }

  try {
    return await sb.groupChannel.getTotalUnreadMessageCount()
  } catch (err) {
    datadogRum.addError(err as Error, {
      source: 'sendbird-rest-client',
      action: 'fetch_unread_count',
    })
    throw err
  }
}
